import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDrag, useDrop } from "react-dnd";
import type { Identifier } from "dnd-core";
import { DragIndicator } from "@mui/icons-material";
import { TextField } from "@mui/material";
import { Button, DataDialog } from "@app/components/atoms";
import { IconDelete, IconInputError } from "@app/components/icons";
import { REGEX, safeSliceGraphemes } from "@app/helpers";

export type PredefinedCommentOption = {
  name: string;
  position: number;
  customInspectionItemsOptionsId?: string;
};

export const MAX_PREDEFINED_COMMENTS = 10;

const PREDEFINED_COMMENT_ITEM = "PREDEFINED_COMMENT_ITEM";

type DragItem = {
  index: number;
};

type RowErrorType = "required" | "hasEmoji" | "hasInvalidChar";

type CommentRow = PredefinedCommentOption & {
  rowKey: number;
  touched: boolean;
};

function getRowError(value: string): RowErrorType | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return "required";
  }
  if (!REGEX.emojiRegex.test(trimmed)) {
    return "hasEmoji";
  }
  if (!REGEX.commonNameFieldRegex.test(trimmed)) {
    return "hasInvalidChar";
  }
  return undefined;
}

type OptionRowProps = {
  index: number;
  value: string;
  showError: boolean;
  errorType?: RowErrorType;
  errorMessage?: string;
  canDelete: boolean;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
  onChange: (value: string) => void;
  onBlur: () => void;
  onDelete: () => void;
};

function OptionRow({
  index,
  value,
  showError,
  errorType,
  errorMessage,
  canDelete,
  moveRow,
  onChange,
  onBlur,
  onDelete,
}: OptionRowProps) {
  const { t } = useTranslation();

  const [{ isDragging }, drag] = useDrag({
    type: PREDEFINED_COMMENT_ITEM,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: PREDEFINED_COMMENT_ITEM,
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
    }),
    hover(item) {
      if (item.index === index) {
        return;
      }
      moveRow(item.index, index);
      item.index = index;
    },
  });

  return (
    <div className="predefined-comment-row-wrap">
      <div
        ref={drop}
        className="predefined-comment-row"
        style={{ opacity: isDragging ? 0.5 : 1 }}
      >
        <span ref={drag} className="predefined-comment-drag-handle">
          <DragIndicator className="drag-icon predefined-comment-drag" />
        </span>
        <div className="wrapper-text-field predefined-comment-input">
          <TextField
            className="input-field"
            label={t("predefinedCommentName")}
            value={value}
            error={showError}
            onChange={(e) => onChange(safeSliceGraphemes(e.target.value, 100))}
            onBlur={(e) => {
              onChange(e.target.value.trim());
              onBlur();
            }}
          />
          {showError && <IconInputError />}
        </div>
        <Button
          className="btn-no-border-red predefined-comment-delete"
          disabled={!canDelete}
          onClick={onDelete}
        >
          <IconDelete />
        </Button>
      </div>
      {showError && errorMessage && (
        <p className="txt-red fs-12 mt-0 mb-0 predefined-comment-row-error">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

type PredefinedCommentsDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialOptions: PredefinedCommentOption[];
  onSave: (options: PredefinedCommentOption[]) => void;
};

export function PredefinedCommentsDialog({
  open,
  setOpen,
  initialOptions,
  onSave,
}: PredefinedCommentsDialogProps) {
  const { t } = useTranslation();
  const nextRowKey = useRef(0);
  const [rows, setRows] = useState<CommentRow[]>([]);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setShowErrors(false);
    // Keep every returned row visible so users can repair an oversized API list.
    setRows(
      (initialOptions.length > 0
        ? [...initialOptions].sort((a, b) => a.position - b.position)
        : [{ name: "", position: 1 }]
      ).map((option) => ({
        ...option,
        rowKey: nextRowKey.current++,
        touched: false,
      })),
    );
  }, [open, initialOptions]);

  const moveRow = useCallback((dragIndex: number, hoverIndex: number) => {
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(hoverIndex, 0, moved);
      return next;
    });
  }, []);

  const handleAddRow = () => {
    const rowKey = nextRowKey.current++;
    setRows((prev) => {
      if (prev.length >= MAX_PREDEFINED_COMMENTS) {
        return prev;
      }
      return [
        ...prev,
        { name: "", position: prev.length + 1, rowKey, touched: false },
      ];
    });
  };

  const handleDeleteRow = (index: number) => {
    setRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const rowErrors = useMemo(
    () => rows.map((row) => getRowError(row.name)),
    [rows],
  );

  const isSaveDisabled =
    rows.length === 0 ||
    rows.length > MAX_PREDEFINED_COMMENTS ||
    rowErrors.some((error) => error !== undefined);

  const getErrorMessage = (errorType?: RowErrorType) => {
    if (errorType === "required") {
      return t("messages.require");
    }
    if (errorType === "hasEmoji" || errorType === "hasInvalidChar") {
      return t("messages.validCharacters");
    }
    return undefined;
  };

  const handleSave = () => {
    setShowErrors(true);

    if (isSaveDisabled) {
      return;
    }

    onSave(
      rows.map((row, index) => {
        return {
          name: row.name.trim(),
          position: index + 1,
          ...(row.customInspectionItemsOptionsId
            ? {
                customInspectionItemsOptionsId:
                  row.customInspectionItemsOptionsId,
              }
            : {}),
        };
      }),
    );
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <DataDialog
      title={t("predefinedComment")}
      open={open}
      setOpen={setOpen}
      disableBackdropClick
      className="dialog-predefined-comments"
      dialogActions={
        <div className="flex align-items-center justify-content-end w-100">
          <Button className="btn-no-border" onClick={handleClose}>
            {t("dialogReport.cancel")}
          </Button>
          <Button
            className="btn-primary ml-15"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            {t("formAction.save")}
          </Button>
        </div>
      }
    >
      <div className="predefined-comments-dialog">
        <p className="predefined-comment-count txt-gray-3 fs-14 mb-16 mt-0">
          {t("predefinedCommentCount", {
            count: rows.length,
            max: MAX_PREDEFINED_COMMENTS,
          })}
        </p>

        {rows.map((row, index) => {
          const errorType = rowErrors[index];
          const shouldShowError =
            !!errorType && (showErrors || row.touched);

          return (
            <OptionRow
              key={row.rowKey}
              index={index}
              value={row.name}
              showError={shouldShowError}
              errorType={errorType}
              errorMessage={getErrorMessage(errorType)}
              canDelete={rows.length > 1}
              moveRow={moveRow}
              onChange={(value) => {
                setRows((prev) =>
                  prev.map((current, rowIndex) =>
                    rowIndex === index ? { ...current, name: value } : current,
                  ),
                );
              }}
              onBlur={() => {
                setRows((prev) =>
                  prev.map((current, rowIndex) =>
                    rowIndex === index ? { ...current, touched: true } : current,
                  ),
                );
              }}
              onDelete={() => handleDeleteRow(index)}
            />
          );
        })}

        <div className="flex-centered mt-24">
          <Button
            className="btn-secondary"
            disabled={rows.length >= MAX_PREDEFINED_COMMENTS}
            onClick={handleAddRow}
          >
            {t("addPredefinedComment")}
          </Button>
        </div>
      </div>
    </DataDialog>
  );
}
